import { KeyRoundIcon, MailIcon, QrCodeIcon, SparklesIcon } from 'lucide-react'
import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { AuthShell } from '../components/AuthShell'
import {
  PreferenceButton,
  PreferenceCategory,
  PreferenceHeader,
  PreferenceInfo,
  PreferenceLink,
} from '../components/Preference'
import {
  needsHousehold,
  signInWithGoogle,
  DEFAULT_APP_PATH,
  useConfig,
  useMe,
} from '../lib/auth-client'

/** First-run gate: start household, sign in, or scan invite QR. */
export default function Welcome() {
  const navigate = useNavigate()
  const location = useLocation()
  const { data: me, isLoading } = useMe()
  const { data: config } = useConfig()
  const googleEnabled = config?.googleEnabled ?? false

  useEffect(() => {
    if (!me?.user) return
    // Anonymous without a household can still use Start from here.
    if (me.user.isAnonymous && me.user.tenantId == null) return
    if (needsHousehold(me)) {
      navigate('/onboarding', { replace: true })
      return
    }
    const from = (
      location.state as { from?: { pathname: string; search?: string } } | null
    )?.from
    const dest =
      from?.pathname && from.pathname !== '/welcome'
        ? `${from.pathname}${from.search ?? ''}`
        : DEFAULT_APP_PATH
    navigate(dest, { replace: true })
  }, [me, navigate, location.state])

  // Guests and anonymous-without-tenant stay; everyone else is bouncing away.
  const bouncing =
    isLoading ||
    !me ||
    (me.user != null && (me.user.tenantId != null || !me.user.isAnonymous))

  if (bouncing) {
    return (
      <AuthShell>
        <PreferenceInfo title="Laden…" />
      </AuthShell>
    )
  }

  return (
    <AuthShell>
      <PreferenceHeader>Menu</PreferenceHeader>

      <PreferenceCategory filled title="Ik ben nieuw">
        <PreferenceLink
          icon={<SparklesIcon className="h-6 w-6" />}
          title="Start"
          summary="Nieuw huishouden zonder account"
          href="/start"
        />
        <PreferenceLink
          icon={<QrCodeIcon className="h-6 w-6" />}
          title="Scan uitnodigings-QR"
          summary="Deelname via QR van familie"
          href="/join"
        />
      </PreferenceCategory>

      <PreferenceCategory filled title="Ik heb al een account">
        {googleEnabled ? (
          <PreferenceButton
            icon={<GoogleIcon className="h-6 w-6" />}
            title="Inloggen met Google"
            onPress={() => void signInWithGoogle(DEFAULT_APP_PATH)}
          />
        ) : null}
        <PreferenceLink
          icon={<MailIcon className="h-6 w-6" />}
          title="Inloggen met e-mailadres"
          summary="Ontvang een inloglink"
          href="/login/email"
        />
        <PreferenceLink
          icon={<KeyRoundIcon className="h-6 w-6" />}
          title="Inloggen met wachtwoord"
          href="/login"
        />
      </PreferenceCategory>
    </AuthShell>
  )
}

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg
      role="img"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      fill="currentColor"
      aria-hidden
    >
      <title>Google</title>
      <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z" />
    </svg>
  )
}
